import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Moon, Sunset, Star, Heart } from 'lucide-react';

const faqs = [
  {
    q: "Is Rudragarh suitable for a honeymoon?",
    a: "Absolutely. Rudragarh is a boutique property — small scale, personal service, and no resort-hotel crowds. The cave stay is one of the most genuinely romantic rooms in all of Rajasthan: carved into rock, lit by lanterns, surrounded by the desert silence. Add private sand dunes at sunset, stargazing nights with no light pollution, and a heritage dining hall for candlelit dinners — it's an environment that creates memories, not just a stay."
  },
  {
    q: "What romantic experiences does Rudragarh offer?",
    a: "Beyond the rooms themselves, Rudragarh's private sand dunes are the centrepiece — no crowds, no tour groups, just you and the Thar at golden hour. Evenings bring cultural programs and open-air theatre under the stars. The natural pond and waterfall on the property offer quiet moments during the day. Candlelit dining in the traditional low-sitting heritage hall makes every evening feel ceremonial. And the desert sky at night, far from city light, is something most couples have never experienced together."
  },
  {
    q: "What is the best room for a honeymoon at Rudragarh?",
    a: "The Catacomb Suite is our most recommended honeymoon room — subterranean carved-cave cottages with natural climate control, unlike anything else in India, the desert as your world. For couples who want more space and grandeur, The Grand Quad is the resort's grandest room with a king bed, steps from the bar, pool and garden. Cascade Cottages are perfect if you want charm and calm — a private water-facing cottage with a traditional Jharoka window seat and cascade pond and waterfall views."
  }
];

const highlights = [
  {
    icon: <Moon size={28} />,
    title: "Cave Stay — Most Romantic Room",
    desc: "Carved rock walls, lantern light, desert silence. The cave stay at Rudragarh is unlike any hotel room — it's an experience couples talk about for years."
  },
  {
    icon: <Star size={28} />,
    title: "Stargazing on Private Dunes",
    desc: "No light pollution, no crowds. The Thar desert sky at night is extraordinary — a natural planetarium that costs nothing and impresses everyone."
  },
  {
    icon: <Heart size={28} />,
    title: "Candlelit Heritage Dining",
    desc: "Dinners in the traditional low-sitting hall feel ceremonial. The live kitchen, local flavours, and intimate atmosphere set the tone for the entire evening."
  },
  {
    icon: <Sunset size={28} />,
    title: "Private Dunes at Sunset",
    desc: "Rudragarh's own sand dunes mean no strangers walking through your golden-hour moment. Sunset here is private, unhurried, and genuinely stunning."
  }
];

const rooms = [
  {
    slug: "catacomb-suite",
    name: "The Catacomb Suite",
    price: 10000,
    guests: 4,
    category: "unique",
    desc: "Our most romantic room — carved into rock, lit by lanterns, wrapped in desert silence. There is nowhere else in Jaisalmer quite like this."
  },
  {
    slug: "grand-quad",
    name: "The Grand Quad",
    price: 7000,
    guests: 4,
    category: "modern",
    desc: "A king bed, generous space, and a sense of occasion. Ideal for honeymooners who want comfort and grandeur alongside the desert experience."
  },
  {
    slug: "cascade-cottages",
    name: "Cascade Cottages",
    price: 12000,
    guests: 2,
    category: "heritage",
    desc: "Wake to birdsong and pond views. Heritage architecture, Rajasthani charm, and a quiet intimacy that suits a couple perfectly."
  }
];

const Honeymoon = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Honeymoon in Jaisalmer | Romantic Desert Escape at Rudragarh</title>
        <meta name="description" content="Plan your perfect Jaisalmer honeymoon at Rudragarh Resort — cave stays, private dunes, candlelit dinners, stargazing, and boutique luxury near Sam Sand Dunes." />
        <meta property="og:title" content="Honeymoon in Jaisalmer | Romantic Desert Escape at Rudragarh" />
        <meta property="og:description" content="Plan your perfect Jaisalmer honeymoon at Rudragarh Resort — cave stays, private dunes, candlelit dinners, stargazing, and boutique luxury near Sam Sand Dunes." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/for/honeymoon" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Honeymoon in Jaisalmer | Rudragarh Resort" />
        <meta name="twitter:description" content="Plan your perfect Jaisalmer honeymoon at Rudragarh — cave stays, private dunes, candlelit dinners, and stargazing." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/for/honeymoon" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pool-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Jaisalmer, Rajasthan</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Your Honeymoon in <span className="text-gold-500">Jaisalmer</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              A cave carved into the desert. Private dunes at sunset. A sky full of stars. Rudragarh is where honeymoons become stories worth telling.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="overline">For Newlyweds</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Not a resort honeymoon. A <span className="text-gold-500">real one.</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed">
              Most honeymoon packages are the same thing repackaged — a rose-petal turndown, a fruit basket, a pool that's busy from noon. Rudragarh is something different. It's boutique by design, which means you don't share your sunset with a hundred other guests. Your dunes are your own. Your evenings are quiet. The staff know your names.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              The cave stay is our signature — carved into rock, lit by lanterns, surrounded by the silence of the Thar. Nothing in Jaisalmer offers what this room offers. Wake up inside a desert cave and step out onto private sand dunes. It's the kind of beginning that sets the tone for everything that follows.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              Candlelit dinners in the traditional hall. Stargazing on the dunes at midnight. A natural pond and waterfall that most guests stumble upon and never want to leave. Your honeymoon at Rudragarh will be remembered for what it felt like — not just what it looked like.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Why Couples Choose Rudragarh</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Made for <span className="text-gold-500">Romance</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Where You'll Stay</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rooms Built for <span className="text-gold-500">Two</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-xl mx-auto">
              From a carved cave lit by lanterns to a king suite and a heritage cottage with pond views — every room at Rudragarh offers intimacy by design.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Common Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Honeymoon at <span className="text-gold-500">Rudragarh</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pool-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Begin Your Story</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            Start Together in the Desert
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Your honeymoon is a once-in-a-lifetime moment. Choose a setting that's just as rare — a desert cave, private dunes, and the skies of Jaisalmer.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book Your Honeymoon Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Honeymoon;
